import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import {
  getFormFieldProps,
  getFormFieldWrapperProps,
  getValidationFunction,
} from '@/formbuilder/formbuilder.utilities';
import { Select } from '@seeqdev/qomponents';
import type { ReactNode } from 'react';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface SelectOption<T = string | number | null | undefined> {
  label: string;
  value: T;
}

type SelectValue = string | number | null | undefined;

export interface SelectIF extends Omit<ValidatingFormComponent<SelectValue | SelectValue[]>, 'onChange'> {
  component: 'SelectFormComponent';
  value: SelectValue | SelectValue[];
  options: SelectOption<SelectValue>[];
  placeholder?: string;
  required?: boolean;
  popoverContent?: ReactNode;
  insideModal?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  creatable?: boolean;
  isMulti?: boolean;
  onTooltipClick?: () => void;
  onChange?: BivariantCallback<SelectValue | SelectValue[] | undefined>;
  allowClear?: boolean;
  closeMenuOnSelect?: boolean;
  controlClassNames?: string;
  externalGetOptionLabel?: (option: SelectOption<SelectValue>) => ReactNode | string | undefined;
  externalGetSelectedLabel?: (option: SelectOption<SelectValue>) => ReactNode | string | undefined;
}

type BivariantCallback<T> = { bivarianceHack(value: T): void }['bivarianceHack'];

/**
 * Native select form component with optional tool-tip/popover
 */
export const SelectFormComponent = (props: SelectIF) => {
  const {
    name,
    testId = 'select',
    label,
    options,
    validation,
    extendValidation,
    extraClassNames,
    wrapperClassNames,
    customErrorText,
    placeholder,
    required,
    popoverContent,
    value,
    insideModal = false,
    disabled = false,
    searchable = false,
    creatable = false,
    isMulti = false,
    tooltip,
    onTooltipClick,
    tooltipPlacement,
    skipToFormState = false,
    allowClear = false,
    closeMenuOnSelect = true,
    controlClassNames,
    externalGetOptionLabel,
    externalGetSelectedLabel,
  } = props;

  const defaultValidation = (value: SelectValue | SelectValue[] | undefined) =>
    required ? _.isNil(value) || (_.isArray(value) ? _.isEmpty(value) : value === '') : false;

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const findSelectedLabel = () => _.find(options, (option) => option.value === value)?.label;

  const { t } = useTranslation();
  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    (_.has(formState.dirtyFields, name) || _.has(formState.dirtyFieldsSinceLastSubmit, name)) &&
    formState.hasValidationErrors;
  const [selectedLabel, setSelectedLabel] = useState(findSelectedLabel());

  useEffect(() => {
    setSelectedLabel(findSelectedLabel() || selectedLabel);
  }, [options, value]);

  const getOptionLabel = (option: SelectOption<SelectValue>) =>
    externalGetOptionLabel ? externalGetOptionLabel(option) : `${t(option.label)}`;

  const getOptionValue = (option: SelectOption<SelectValue>) => String(option.value ?? '');

  const handleChange =
    (onChangeFn: (value: SelectValue | SelectValue[]) => void) => (selected: SelectOption<SelectValue> | null) =>
      onChangeFn(selected?.value);
  const handleChangeIfMulti =
    (onChangeFn: (value: SelectValue | SelectValue[]) => void) =>
    (selected: SelectOption<SelectValue>[] = []) =>
      onChangeFn(_.map(selected, (each) => each.value));

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props)}
      testId={testId}
      label={label}
      wrapperClassNames={clsx(wrapperClassNames, 'flexFillOverflow')}
      extraClassNames={extraClassNames}
      showError={showError}
      customErrorText={customErrorText}
      popoverContent={popoverContent}
      tooltip={tooltip}
      onTooltipClick={onTooltipClick}
      tooltipPlacement={tooltipPlacement}>
      <Field<SelectValue | SelectValue[]> name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const formFieldProps = getFormFieldProps(formState, input, meta, {
            ...props,
            onChange: props.onChange as BivariantCallback<SelectValue | SelectValue[]>,
          });
          const formFieldValue = skipToFormState ? formFieldProps.value : value;
          const hasValue = (formFieldValue ?? '') !== '';
          const label = hasValue
            ? (_.find(options, (option) => option.value === formFieldValue)?.label ?? String(formFieldValue ?? ''))
            : '';
          const getValueIfMulti = () =>
            Array.isArray(formFieldProps.value)
              ? _.map(formFieldProps.value, (each) => {
                  const option = _.find(options, (opt) => opt.value === each);
                  return { label: String(option?.label ?? each ?? ''), value: each };
                })
              : undefined;
          const getValue = () => (hasValue ? { value: formFieldValue, label } : null);

          return (
            <Select
              showError={formFieldProps.showError}
              placeholder={placeholder ? t(placeholder) : undefined}
              value={isMulti ? getValueIfMulti() : getValue()}
              options={options}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              getSelectedValueLabel={externalGetSelectedLabel}
              onChange={isMulti ? handleChangeIfMulti(formFieldProps.onChange) : handleChange(formFieldProps.onChange)}
              creatable={creatable}
              isMulti={isMulti}
              isSearchable={searchable}
              menuPortalTarget={document.body}
              closeMenuOnSelect={closeMenuOnSelect}
              isDisabled={disabled}
              isClearable={allowClear}
              controlClassNames={controlClassNames}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
